import React, { useState } from "react";
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiPlus,
  FiMenu,
  FiX,
  FiCamera,
  FiUpload,
} from "react-icons/fi";
import { Button } from "./Button";
import { MenuButton } from "./MenuButton";

interface NavigationProps {
  logoSrc?: string;
  logoText?: string;
}

const NavHeader = styled.nav`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: ${({ theme }) => theme.colors.navBackground};
  display: flex;
  align-items: center;
  padding: 0 32px;
  z-index: 1000;
  box-shadow: ${({ theme }) => theme.shadows.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 16px;
  }
`;

const NavContainer = styled.div`
  width: 100%;
  /* max-width: 1200px; */
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  img {
    height: 32px;
    width: auto;
  }

  span {
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.25rem;
    font-weight: 600;
    font-family: ${({ theme }) => theme.fontFamily.heading};
    letter-spacing: 0.5px;
  }
`;

const DesktopNav = styled.div`
  display: none;
  align-items: center;
  gap: 32px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.muted};
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.background};
  transform: translateX(${({ $isOpen }) => ($isOpen ? "0" : "100%")});
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  padding: 24px;
  z-index: 999;
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MobileNavLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text};
  text-decoration: none;
  font-weight: ${({ $active }) => ($active ? "600" : "500")};
  font-size: 1.125rem;
  padding: 21px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

// const ActionButton = styled(Button)`
//   @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
//     margin-top: 24px;
//     width: 100%;
//   }
// `;

export const NewNavigation: React.FC<NavigationProps> = ({
  logoSrc = "/verae-logo-black.svg",
  logoText = "Verae",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <>
      <NavHeader>
        <NavContainer>
          <Link to="/">
            <Logo>
              <img src={logoSrc} alt={logoText} />
              {/* <span>{logoText}</span> */}
            </Logo>
          </Link>

          <DesktopNav>
            <div style={{ marginLeft: "16px" }}>
              <MenuButton
                variant="primary"
                size="md"
                icon={<FiPlus size={18} />}
                options={[
                  {
                    label: "Image Scan",
                    onClick: () => navigate("/record/scanner"),
                    icon: <FiCamera size={16} />,
                  },
                  {
                    label: "Manual Entry",
                    onClick: () => navigate("/record/manual"),
                    icon: <FiUpload size={16} />,
                  },
                ]}
              >
                Add Record
              </MenuButton>
            </div>
          </DesktopNav>

          <MobileMenuButton onClick={toggleMenu}>
            {isOpen ? <FiX /> : <FiMenu />}
          </MobileMenuButton>
        </NavContainer>
      </NavHeader>

      <MobileMenu $isOpen={isOpen}>
        <MobileNavLinks>
          <MobileNavLink
            to="/"
            $active={location.pathname === "/"}
            onClick={closeMenu}
          >
            <FiSearch style={{ marginRight: "12px" }} /> Search
          </MobileNavLink>
          <MobileNavLink
            to="/record/manual"
            $active={location.pathname === "/record/manual"}
            onClick={closeMenu}
          >
            <FiUpload style={{ marginRight: "12px" }} /> Add Record Manually
          </MobileNavLink>
          <MobileNavLink
            to="/record/scanner"
            $active={location.pathname === "/record/scanner"}
            onClick={closeMenu}
          >
            <FiCamera style={{ marginRight: "12px" }} /> Add Record with Scanner
          </MobileNavLink>
          {/* <MobileNavLink
            to="/scanner"
            $active={location.pathname === "/scanner"}
            onClick={closeMenu}
          >
            <FiCamera style={{ marginRight: "12px" }} /> Scanner
          </MobileNavLink>
          <MobileNavLink
            to="/records"
            $active={location.pathname === "/records"}
            onClick={closeMenu}
          >
            Records
          </MobileNavLink> */}

          {/* <ActionButton
            variant="primary"
            size="lg"
            icon={<FiPlus size={18} />}
            onClick={() => {
              closeMenu();
              navigate("/scanner");
              // Navigate programmatically if needed
            }}
          >
            Add Record
          </ActionButton> */}
        </MobileNavLinks>
      </MobileMenu>
    </>
  );
};
